import { BELT_COLOR } from "@kamae-apps/shared/Types/Belt"
import { COMPANY_STATUS } from "@kamae-apps/shared/Types/Company"
import { formatInTimeZone } from "date-fns-tz"

const beltLevelMap = new Map<BELT_COLOR, number>([
  [BELT_COLOR.WHITE, 5],
  [BELT_COLOR.YELLOW, 11],
  [BELT_COLOR.ORANGE, 16],
  [BELT_COLOR.GREEN, 20],
  [BELT_COLOR.BLUE, 24],
  [BELT_COLOR.PURPLE, 27],
  [BELT_COLOR.BROWN, 31],
  [BELT_COLOR.BLACK, 34],
  [BELT_COLOR.RED, 37],
  [BELT_COLOR.BRONZE, 39],
  [BELT_COLOR.SILVER, 42],
  [BELT_COLOR.GOLD, 44],
  [BELT_COLOR.GOD, 10000],
])

export function getNextLevel(belt: BELT_COLOR) {
  return beltLevelMap.get(belt) ?? 5
}

export function formatDate(date: Date, displayHours = true, displaySeconds = false): string {
  const lang = localStorage.getItem("lang") ?? "fr"
  if (lang !== "en") {
    return formatInTimeZone(
      date,
      "Europe/Paris",
      `dd/MM/yyyy ${displayHours ? `HH:mm${displaySeconds ? ":ss" : ""}` : ""}`
    )
  }
  return formatInTimeZone(
    date,
    "Europe/Paris",
    `MM/dd/yyyy ${displayHours ? `hh:mm${displaySeconds ? ":ss" : ""} aaa` : ""}`
  )
}

export function isNil(value: unknown): boolean {
  return value === null || value === undefined
}

export function isBlank(value: string): boolean {
  return value.trim() === ""
}

export function isNilOrBlank(value: unknown): boolean {
  return isNil(value) || (typeof value === "string" && isBlank(value))
}

export function validateEmail(email: string): boolean {
  return /^[\w\d.'-]+@[\w\d.-]+\.[\w\d_-]{2,}$/.test(email)
}

const codeToLang = new Map<number, string>([
  [1, "fr"],
  [2, "en"],
  [3, "es"],
  [4, "de"],
  [5, "pt"],
  [6, "it"],
])

export function fromCodeToLang(code: number): string {
  return codeToLang.get(code) ?? "fr"
}

// Hash a string using SHA-1
export async function sha1(message: string) {
  const msgUint8 = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest("SHA-1", msgUint8)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("")
}

export function shouldThePathBeMemoized(path: string) {
  return path !== "/" && path !== "/login"
}

export const getCompanyPlan = (companyStatus: COMPANY_STATUS): string => {
  switch (companyStatus) {
    case COMPANY_STATUS.CUSTOMER:
      return "Essential"
    case COMPANY_STATUS.STARTER:
      return "Starter"
    case COMPANY_STATUS.TRIAL:
      return "Trial"
    case COMPANY_STATUS.KAMAE:
      return "Kamae"
    case COMPANY_STATUS.CHURNED:
      return "Churned"
    default:
      return "Unknown"
  }
}

export const secondsToMinSec = (timerSecond: number): string => {
  const seconds = Math.max(0, Math.floor(timerSecond))
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${minutes}:${String(secs).padStart(2, "0")}`
}

export function toCamelCase(str: string): string {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase()
    })
    .replace(/\s+/g, "")
}
